import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, AlertCircle, Loader2, Check, Warehouse, Info, ChevronDown } from 'lucide-react';
import { useOrders } from '../hooks/useOrders';
import orderService from '../services/order.service';
import warehouseService from '../services/warehouse.service';
import miscService from '../services/misc.service';
import { useWallet } from '../hooks/useWallet';
import { useShippingMode } from '../context/ShippingModeContext';
import { sanitizeError } from '../utils/errorUtils';
import RechargeModal from '../components/Wallet/RechargeModal';
import PriceBreakdownModal from '../components/PriceBreakdownModal';
import BankAccountModal from '../components/BankAccountModal';
import KYCGuard from '../components/KYCGuard';
import api from '../config/api';
import pincodeService from '../services/pincode.service';
import { useAuth } from '../hooks/useAuth';

// InputField component moved outside to prevent recreation on every render
const InputField = React.memo(({ label, value, onChange, error, placeholder, type = 'text', required = false, maxLength = null, min = null, step = null, list = null, listId = null, international = false, unit = null, onUnitChange = null, onBlur = null }) => {
  return (
    <div className="space-y-1 relative">
      <label className="text-xs font-medium text-gray-700">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <div className="flex gap-2">
        <input
          type={type}
          value={value || ''}
          list={listId}
          onChange={(e) => {
            let inputValue = e.target.value;
            // Handle phone and pincode length restrictions
            // (international phones keep country code and up to E.164 length)
            if (type === 'tel' && !international && inputValue.length > 10) {
              inputValue = inputValue.slice(0, 10);
            } else if (label.toLowerCase().includes('pincode') && !international && inputValue.length > 6 && !label.toLowerCase().includes('country')) {
              inputValue = inputValue.slice(0, 6);
            }
            onChange(inputValue);
          }}
          placeholder={placeholder}
          maxLength={maxLength}
          min={min}
          step={step}
          onBlur={onBlur}
          className={`w-full border rounded-md p-2 text-sm focus:outline-none focus:ring-2 focus:border-blue-500 ${error ? 'border-red-500 focus:ring-red-200' : 'border-gray-300 focus:ring-blue-200'
            }`}
          style={{ pointerEvents: 'auto', zIndex: 1 }}
        />
        {unit && (
          <select
            value={unit}
            onChange={(e) => onUnitChange?.(e.target.value)}
            className="shrink-0 border border-gray-300 rounded-md px-2 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-500"
            style={{ pointerEvents: 'auto', zIndex: 1 }}
          >
            <option value="kg">kg</option>
            <option value="g">g</option>
          </select>
        )}
      </div>
      {list && listId && (
        <datalist id={listId}>
          {list.map((item, index) => (
            <option key={index} value={item.name || item} />
          ))}
        </datalist>
      )}
      {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
    </div>
  );
}, (prevProps, nextProps) => {
  // Only re-render if value, error, or label changes (ignore onChange reference)
  return (
    prevProps.value === nextProps.value &&
    prevProps.error === nextProps.error &&
    prevProps.label === nextProps.label &&
    prevProps.placeholder === nextProps.placeholder &&
    prevProps.type === nextProps.type &&
    prevProps.international === nextProps.international
  );
});
InputField.displayName = 'InputField';

const CreateOrder = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { calculateRate, createOrder, loading, error } = useOrders();
  const { balance, fetchBalance } = useWallet();
  const { shippingMode, getDeliveryPartners, isInternational, isDomestic } = useShippingMode();
  const { user } = useAuth();

  const getDefaultFormData = () => ({
    pickupDetails: {
      name: '',
      phone: '',
      address: '',
      pincode: '',
      city: '',
      state: '',
      country: 'India',
      companyName: user?.companyName || '',
      consigner_gst_applicable: '',
      consigner_tax_payment: '',
      export_type: '',
      tax_paid: '',
      tax_amount: ''
    },
    deliveryDetails: {
      name: '',
      phone: '',
      address: '',
      pincode: '',
      city: '',
      state: '',
      country: isInternational ? '' : 'India',
      vatId: '',
      iossNumber: '',
      invoiceNumber: '',
      booking_invoice_date: ''
    },
    packageDetails: {
      weight: '',
      dimensions: {
        length: '',
        width: '',
        height: ''
      },
      description: '',
      declaredValue: '',
      packageType: 'Box',
      shipmentType: 1, // 1=Non-Doc, 2=Doc, 4=Commercial
      currency: '24', // Default to INR (ID 24)
      cargoType: '',
      clearanceType: '',
      incoterm: ''
    },
    products: [{
      description: '',
      hsnCode: '',
      qty: 1,
      unitRate: '',
      pieceWt: ''
    }]
  });

  const [formData, setFormData] = useState(getDefaultFormData);
  const [weightUnit, setWeightUnit] = useState('kg'); // 'kg' | 'g' — formData.weight always stays kg; converted only at the input

  const [countries, setCountries] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [reverseOriginalId, setReverseOriginalId] = useState(null);

  useEffect(() => {
    const fetchInitialData = async () => {
      const [countryList, currencyList] = await Promise.all([
        miscService.getCountries(),
        miscService.getCurrencies()
      ]);
      setCountries(countryList);
      setCurrencies(currencyList);
    };
    fetchInitialData();
  }, []);

  // Pre-fill form for reverse orders
  useEffect(() => {
    const reverseFrom = searchParams.get('reverseFrom');
    if (!reverseFrom) return;

    const prefillReverseOrder = async () => {
      try {
        const response = await orderService.getOrderById(reverseFrom);
        const original = response.data?.order || response.order || response;
        if (!original) return;

        setReverseOriginalId(original._id);

        setFormData(prev => ({
          ...prev,
          pickupDetails: {
            ...prev.pickupDetails,
            name: original.deliveryDetails?.name || '',
            phone: original.deliveryDetails?.phone || '',
            email: original.deliveryDetails?.email || '',
            address: original.deliveryDetails?.address || '',
            addressLine2: original.deliveryDetails?.addressLine2 || '',
            pincode: original.deliveryDetails?.pincode || '',
            city: original.deliveryDetails?.city || '',
            state: original.deliveryDetails?.state || '',
            country: original.deliveryDetails?.country || 'India',
            companyName: original.deliveryDetails?.companyName || '',
            gstin: original.deliveryDetails?.gstin || ''
          },
          deliveryDetails: {
            ...prev.deliveryDetails,
            name: original.pickupDetails?.name || '',
            phone: original.pickupDetails?.phone || '',
            email: original.pickupDetails?.email || '',
            address: original.pickupDetails?.address || '',
            addressLine2: original.pickupDetails?.addressLine2 || '',
            pincode: original.pickupDetails?.pincode || '',
            city: original.pickupDetails?.city || '',
            state: original.pickupDetails?.state || '',
            country: original.pickupDetails?.country || 'India',
            companyName: original.pickupDetails?.companyName || '',
            vatId: original.pickupDetails?.vatId || '',
            iossNumber: original.pickupDetails?.iossNumber || ''
          },
          packageDetails: {
            ...prev.packageDetails,
            weight: original.packageDetails?.weight || '',
            dimensions: {
              length: original.packageDetails?.dimensions?.length || '',
              width: original.packageDetails?.dimensions?.width || '',
              height: original.packageDetails?.dimensions?.height || ''
            },
            description: original.packageDetails?.description || '',
            declaredValue: original.packageDetails?.declaredValue || '',
            packageType: original.packageDetails?.packageType || 'Box',
            shipmentType: original.packageDetails?.shipmentType || 1,
            currency: original.packageDetails?.currency || '24',
            cargoType: original.packageDetails?.cargoType || '',
            clearanceType: original.packageDetails?.clearanceType || '',
            incoterm: original.packageDetails?.incoterm || ''
          },
          products: original.products?.length ? original.products.map(p => ({
            description: p.description || '',
            hsnCode: p.hsnCode || '',
            qty: p.qty || 1,
            unitRate: p.unitRate || '',
            pieceWt: p.pieceWt || ''
          })) : prev.products,
          deliveryPartner: original.deliveryPartner || ''
        }));
      } catch (err) {
        console.error('Failed to load reverse order:', err);
      }
    };

    prefillReverseOrder();
  }, [searchParams]);

  // Pre-fill pickup company from the logged-in user (only when blank; never on reverse orders)
  useEffect(() => {
    const reverseFrom = searchParams.get('reverseFrom');
    if (reverseFrom || !user?.companyName) return;
    setFormData(prev => prev.pickupDetails.companyName
      ? prev
      : { ...prev, pickupDetails: { ...prev.pickupDetails, companyName: user.companyName } });
  }, [user, searchParams]);

  // Reset conditional export fields when not international
  useEffect(() => {
    if (!isInternational) {
      setFormData(prev => ({
        ...prev,
        pickupDetails: {
          ...prev.pickupDetails,
          export_type: '',
          tax_paid: '',
          tax_amount: ''
        }
      }));
      setFormErrors(prev => {
        const next = { ...prev };
        delete next['pickupDetails.export_type'];
        delete next['pickupDetails.tax_paid'];
        delete next['pickupDetails.tax_amount'];
        return next;
      });
    }
  }, [isInternational]);

  const [paymentType, setPaymentType] = useState('prepaid');

  // COD is not supported for international shipments — force prepaid
  // if the mode is switched after a domestic COD selection.
  useEffect(() => {
    if (isInternational && paymentType === 'cod') {
      setPaymentType('prepaid');
    }
  }, [isInternational, paymentType]);

  const [selectedCourierId, setSelectedCourierId] = useState(null); // composite "provider::id"
  const userPickedCourier = useRef(false);

  // Delhivery International — isolated shipment category picker
  const [delhiveryIntlDetails, setDelhiveryIntlDetails] = useState({
    shipmentCategory: null, // 'non_commercial' | 'commercial'
    shipmentSubType: null,  // 'document' | 'sample' | 'gift' | 'cargo' | 'csb5'
    incoterm: 'FOB',
    invoiceNumber: '',
    invoiceDate: new Date().toISOString().split('T')[0],
    igstPaymentStatus: '',
    lutBondRef: '',
    insuranceValue: '',
    freightValue: '',
    ecommerceExport: false,
    hasIOR: false,
    iorName: '',
    iorPhone: '',
    iorAddress: '',
  });

  const resolveDelhiveryService = (category, subType) => {
    if (category === 'non_commercial') {
      if (subType === 'document') return 'EXPORTS_DOCUMENT';
      if (subType === 'sample') return 'EXPORTS_DEFERRED_EXPRESS';
      if (subType === 'gift') return null;
    }
    if (category === 'commercial') {
      if (subType === 'cargo') return 'EXPORTS_DLV_SAVER';
      if (subType === 'csb5') return 'EXPORTS_EXPRESS';
    }
    return null;
  };

  const rateTimerRef = useRef(null);
  const [rate, setRate] = useState(null);
  const [calculatingRate, setCalculatingRate] = useState(false);
  const [rateError, setRateError] = useState('');

  const [showRechargeModal, setShowRechargeModal] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [breakdownCourier, setBreakdownCourier] = useState(null); // courier object for price breakdown modal
  const [bankModalOpen, setBankModalOpen] = useState(false);
  const [userBankAccount, setUserBankAccount] = useState(undefined); // undefined = not fetched yet
  const [bankHint, setBankHint] = useState('');
  const [kycModal, setKycModal] = useState({ open: false, missing: [], message: '' });
  const [warehouses, setWarehouses] = useState([]);
  const [selectedWarehouseId, setSelectedWarehouseId] = useState('');
  const [clients, setClients] = useState([]);
  const [selectedClientId, setSelectedClientId] = useState('');
  const [collapsedSections, setCollapsedSections] = useState({ pickup: false, delivery: false, product: false });
  const toggleSection = (key) => setCollapsedSections(prev => ({ ...prev, [key]: !prev[key] }));

  // --- Draft persistence: keep the form filled when the user leaves the page
  // (e.g. to order details to schedule pickup) and comes back. Kept after order
  // creation too, so a failed pickup can be fixed without re-typing; cleared
  // only via the "Start fresh" button on the restored-draft notice. ---
  const CREATE_ORDER_DRAFT_KEY = 'createOrderDraft';
  const restoredDraft = useRef(false);
  const [draftRestored, setDraftRestored] = useState(false);
  // Skip the very first save after each mount: it only holds the untouched
  // defaults, and on a restored mount it would briefly clobber the saved draft.
  const skipNextSave = useRef(true);

  // Discard the saved draft and reset the whole form to defaults.
  const startFreshForm = () => {
    sessionStorage.removeItem(CREATE_ORDER_DRAFT_KEY);
    skipNextSave.current = true; // don't re-save the freshly-cleared defaults
    restoredDraft.current = false;
    setDraftRestored(false);
    setFormData(getDefaultFormData());
    setFormErrors({});
    setRate(null);
    setRateError('');
  };

  // Restore a saved draft once on mount.
  useEffect(() => {
    let draft = null;
    try {
      const raw = sessionStorage.getItem(CREATE_ORDER_DRAFT_KEY);
      if (raw) draft = JSON.parse(raw);
    } catch (err) {
      console.warn('Could not read create-order draft:', err);
    }
    if (!draft || typeof draft !== 'object') return;
    restoredDraft.current = true;
    setDraftRestored(true);

    setFormData(prev => ({
      ...prev,
      pickupDetails: { ...prev.pickupDetails, ...(draft.formData?.pickupDetails || {}) },
      deliveryDetails: { ...prev.deliveryDetails, ...(draft.formData?.deliveryDetails || {}) },
      packageDetails: {
        ...prev.packageDetails,
        ...(draft.formData?.packageDetails || {}),
        dimensions: { ...prev.packageDetails.dimensions, ...(draft.formData?.packageDetails?.dimensions || {}) }
      },
      products: draft.formData?.products?.length ? draft.formData.products : prev.products
    }));
    if (draft.paymentType) setPaymentType(draft.paymentType);
    if (draft.delhiveryIntlDetails) setDelhiveryIntlDetails(prev => ({ ...prev, ...draft.delhiveryIntlDetails }));
    if (draft.selectedClientId) setSelectedClientId(draft.selectedClientId);
    if (draft.selectedWarehouseId) setSelectedWarehouseId(draft.selectedWarehouseId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Save the draft on every change so nothing needs to be re-typed.
  useEffect(() => {
    if (skipNextSave.current) {
      skipNextSave.current = false;
      return;
    }
    try {
      sessionStorage.setItem(CREATE_ORDER_DRAFT_KEY, JSON.stringify({
        formData,
        paymentType,
        delhiveryIntlDetails,
        selectedClientId,
        selectedWarehouseId
      }));
    } catch (err) {
      console.warn('Could not save create-order draft:', err);
    }
  }, [formData, paymentType, delhiveryIntlDetails, selectedClientId, selectedWarehouseId]);

  useEffect(() => {
    const fetchState = async (pincode, section) => {
      if (!isDomestic || !pincode || pincode.length !== 6) {
        return;
      }
      try {
        const result = await pincodeService.checkServiceability(pincode);
        if (result && result.success && result.data && result.data.stateName) {
          setFormData(prev => ({
            ...prev,
            [section]: {
              ...prev[section],
              state: result.data.stateName
            }
          }));
        }
      } catch (err) {
        // do nothing
      }
    };

    fetchState(formData.pickupDetails.pincode, 'pickupDetails');
  }, [formData.pickupDetails.pincode, isDomestic]);

  useEffect(() => {
    const fetchState = async (pincode, section) => {
      if (!isDomestic || !pincode || pincode.length !== 6) {
        return;
      }
      try {
        const result = await pincodeService.checkServiceability(pincode);
        if (result && result.success && result.data && result.data.stateName) {
          setFormData(prev => ({
            ...prev,
            [section]: {
              ...prev[section],
              state: result.data.stateName
            }
          }));
        }
      } catch (err) {
        // do nothing
      }
    };

    fetchState(formData.deliveryDetails.pincode, 'deliveryDetails');
  }, [formData.deliveryDetails.pincode, isDomestic]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const whResponse = await warehouseService.getWarehouses();
        if (whResponse.success) {
          setWarehouses(whResponse.data.warehouses);
          const defaultWh = whResponse.data.warehouses.find(w => w.isDefault);
          // Don't overwrite a restored draft's pickup details with warehouse defaults.
          if (defaultWh && !restoredDraft.current) {
            handleWarehouseSelect(defaultWh);
            setSelectedWarehouseId(defaultWh._id);
          }
        }

        const clientService = require('../services/client.service').default;
        const clResponse = await clientService.getClients();
        if (clResponse.success) {
          setClients(clResponse.data.clients);
        }
      } catch (err) {
        console.error('Failed to fetch initial data', err);
      }
    };
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleWarehouseSelect = (wh) => {
    setFormData(prev => ({
      ...prev,
      pickupDetails: {
        ...prev.pickupDetails,
        name: wh.name,
        contactPerson: wh.contactPerson,
        phone: wh.phone,
        email: wh.email || prev.pickupDetails.email,
        address: wh.addressLine1 + (wh.addressLine2 ? ', ' + wh.addressLine2 : ''),
        pincode: wh.pincode,
        city: wh.city,
        state: wh.state,
        country: 'India',
        gstin: wh.gstin || prev.pickupDetails.gstin || ''
      }
    }));
    setFormErrors(prev => {
      const newErrors = { ...prev };
      Object.keys(newErrors).forEach(key => {
        if (key.startsWith('pickupDetails.')) delete newErrors[key];
      });
      return newErrors;
    });
  };

  const handleChange = useCallback((section, field, value) => {
    setFormData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
    setFormErrors(prev => ({
      ...prev,
      [`${section}.${field}`]: ''
    }));
  }, []);

  const handlePackageChange = useCallback((field, value) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setFormData(prev => ({
        ...prev,
        packageDetails: {
          ...prev.packageDetails,
          [parent]: {
            ...prev.packageDetails[parent],
            [child]: value
          }
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        packageDetails: {
          ...prev.packageDetails,
          [field]: value
        }
      }));
    }
    setFormErrors(prev => ({
      ...prev,
      [`packageDetails.${field}`]: ''
    }));
  }, []);

  const handleCargoTypeChange = useCallback((value) => {
    const clearanceType = value === 'CSB5' ? 'INTEGRATOR' : '';
    setFormData(prev => ({
      ...prev,
      packageDetails: {
        ...prev.packageDetails,
        cargoType: value,
        clearanceType,
        incoterm: ''
      }
    }));
    setFormErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors['packageDetails.cargoType'];
      delete newErrors['packageDetails.clearanceType'];
      delete newErrors['packageDetails.incoterm'];
      return newErrors;
    });
  }, []);

  const handleGstApplicableChange = useCallback((value) => {
    const taxPayment = value === '2' ? '3' : '';
    setFormData(prev => ({
      ...prev,
      pickupDetails: {
        ...prev.pickupDetails,
        consigner_gst_applicable: value,
        consigner_tax_payment: taxPayment
      }
    }));
    setFormErrors(prev => {
      const next = { ...prev };
      delete next['pickupDetails.consigner_gst_applicable'];
      delete next['pickupDetails.consigner_tax_payment'];
      return next;
    });
  }, []);

  const handleProductChange = useCallback((index, field, value) => {
    setFormData(prev => {
      const newProducts = [...prev.products];
      newProducts[index] = { ...newProducts[index], [field]: value };
      return { ...prev, products: newProducts };
    });
  }, []);

  const addProduct = useCallback(() => {
    setFormData(prev => ({
      ...prev,
      products: [...prev.products, {
        description: '',
        hsnCode: '',
        qty: 1,
        unitRate: '',
        pieceWt: ''
      }]
    }));
  }, []);

  const removeProduct = useCallback((index) => {
    setFormData(prev => {
      if (prev.products.length <= 1) return prev;
      const newProducts = prev.products.filter((_, i) => i !== index);
      return { ...prev, products: newProducts };
    });
  }, []);

  const validateForm = () => {
    const errors = {};

    // Validate pickup details
    if (!formData.pickupDetails.name.trim()) errors['pickupDetails.name'] = 'Name is required';
    if (!isInternational && !formData.pickupDetails.phone.match(/^[0-9]{10}$/)) {
      errors['pickupDetails.phone'] = 'Valid 10-digit phone required';
    } else if (!formData.pickupDetails.phone.trim()) {
      errors['pickupDetails.phone'] = 'Phone is required';
    }
    if (!formData.pickupDetails.address.trim() || formData.pickupDetails.address.length < 10) errors['pickupDetails.address'] = 'Address must be at least 10 characters';
    if (!isInternational && !formData.pickupDetails.pincode.match(/^[0-9]{6}$/)) errors['pickupDetails.pincode'] = 'Valid 6-digit pincode required';
    if (!formData.pickupDetails.city.trim()) errors['pickupDetails.city'] = 'City is required';
    if (!formData.pickupDetails.state.trim()) errors['pickupDetails.state'] = 'State is required';
    if (!formData.pickupDetails.country.trim()) errors['pickupDetails.country'] = 'Country is required';

    // Validate international export compliance fields (skipped for Document shipments — fields are hidden)
    if (isInternational && formData.packageDetails.shipmentType !== 2) {
      if (!formData.pickupDetails.consigner_gst_applicable) errors['pickupDetails.consigner_gst_applicable'] = 'GST Applicable is required';
      if (!formData.pickupDetails.consigner_tax_payment) errors['pickupDetails.consigner_tax_payment'] = 'Tax Payment is required';
      if (!formData.pickupDetails.export_type) errors['pickupDetails.export_type'] = 'Export Type is required';
      if (formData.pickupDetails.export_type !== '1' && !formData.pickupDetails.tax_paid) errors['pickupDetails.tax_paid'] = 'Tax Paid is required';
      if (formData.pickupDetails.export_type !== '1' && (!formData.pickupDetails.tax_amount || parseFloat(formData.pickupDetails.tax_amount) < 0)) errors['pickupDetails.tax_amount'] = 'Valid Tax Amount is required';
    }

    // Validate delivery details
    if (!formData.deliveryDetails.name.trim()) errors['deliveryDetails.name'] = 'Name is required';
    if (isInternational && !formData.deliveryDetails.phone.match(/^\+?[1-9][0-9]{7,14}$/)) {
      errors['deliveryDetails.phone'] = 'Enter number with or without country code (e.g. +14155550123 or 14155550123)';
    } else if (!isInternational && !formData.deliveryDetails.phone.match(/^[0-9]{10}$/)) {
      errors['deliveryDetails.phone'] = 'Valid 10-digit phone required';
    } else if (!formData.deliveryDetails.phone.trim()) {
      errors['deliveryDetails.phone'] = 'Phone is required';
    }
    if (!formData.deliveryDetails.address.trim() || formData.deliveryDetails.address.length < 10) errors['deliveryDetails.address'] = 'Address must be at least 10 characters';
    if (!isInternational && !formData.deliveryDetails.pincode.match(/^[0-9]{6}$/)) errors['deliveryDetails.pincode'] = 'Valid 6-digit pincode required';
    if (!formData.deliveryDetails.city.trim()) errors['deliveryDetails.city'] = 'City is required';
    if (!formData.deliveryDetails.state.trim()) errors['deliveryDetails.state'] = 'State is required';
    if (!formData.deliveryDetails.country.trim()) errors['deliveryDetails.country'] = 'Country is required';
    if (!formData.deliveryDetails.email?.trim()) errors['deliveryDetails.email'] = 'Email is required';

    // Validate package details
    const weight = parseFloat(formData.packageDetails.weight);
    // Skart international rule: non-doc (1) & commercial (4) need ≥ 0.1 kg; document (2) and other partners only need weight > 0
    const isSkartIntl = isInternational && selectedCourier?.provider === 'skart';
    const needsMinWeight = isSkartIntl && (formData.packageDetails.shipmentType === 1 || formData.packageDetails.shipmentType === 4);
    if (!weight || weight <= 0) {
      errors['packageDetails.weight'] = weightUnit === 'g' ? 'Weight must be greater than 0 g' : 'Weight must be greater than 0 kg';
    } else if (needsMinWeight && weight < 0.1) {
      errors['packageDetails.weight'] = weightUnit === 'g' ? 'Weight must be at least 100 g' : 'Weight must be at least 0.1 kg';
    }

    if (!formData.packageDetails.dimensions.length || parseFloat(formData.packageDetails.dimensions.length) <= 0) errors['packageDetails.dimensions.length'] = 'Length required';
    if (!formData.packageDetails.dimensions.width || parseFloat(formData.packageDetails.dimensions.width) <= 0) errors['packageDetails.dimensions.width'] = 'Width required';
    if (!formData.packageDetails.dimensions.height || parseFloat(formData.packageDetails.dimensions.height) <= 0) errors['packageDetails.dimensions.height'] = 'Height required';

    // Validate international customs fields
    if (isInternational && formData.packageDetails.shipmentType === 4) {
      if (!formData.packageDetails.cargoType) errors['packageDetails.cargoType'] = 'Cargo Type is required';
      if (!formData.packageDetails.clearanceType) errors['packageDetails.clearanceType'] = 'Clearance Type is required';
      if (!formData.packageDetails.incoterm) errors['packageDetails.incoterm'] = 'Incoterm is required';
    }

    formData.products.forEach((product, index) => {
      if (!product.description.trim()) errors[`products.${index}.description`] = 'Required';
      if (!product.hsnCode?.trim()) {
        errors[`products.${index}.hsnCode`] = 'HSN Required';
      } else if (isInternational && !/^[0-9]{8,10}$/.test(product.hsnCode.trim())) {
        errors[`products.${index}.hsnCode`] = 'HSN must be 8-10 digits';
      }
      if (!product.qty || product.qty < 1) errors[`products.${index}.qty`] = 'Min 1';
      if (!product.unitRate || parseFloat(product.unitRate) <= 0) errors[`products.${index}.unitRate`] = 'Required';
      if (!product.pieceWt || parseFloat(product.pieceWt) <= 0) errors[`products.${index}.pieceWt`] = 'Required';
    });

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // --- Auto-collapse: shrink a section on its own once every required field in it
  // is filled and valid. Triggered when the user leaves the section's last field
  // (onBlur); reads the latest form state via a ref so the memoized InputField
  // never holds a stale handler. ---
  const sectionStateRef = useRef({ formData, isInternational });
  sectionStateRef.current = { formData, isInternational };

  const maybeCollapseSection = useCallback((section) => {
    const { formData: fd, isInternational: intl } = sectionStateRef.current;

    const pickupCoreOk = !!(
      fd.pickupDetails.name.trim() &&
      (intl ? !!fd.pickupDetails.phone.trim() : /^[0-9]{10}$/.test(fd.pickupDetails.phone)) &&
      fd.pickupDetails.address.trim() && fd.pickupDetails.address.length >= 10 &&
      (intl || /^[0-9]{6}$/.test(fd.pickupDetails.pincode)) &&
      fd.pickupDetails.city.trim() && fd.pickupDetails.state.trim() && fd.pickupDetails.country.trim()
    );

    const deliveryOk = (() => {
      if (!fd.deliveryDetails.name.trim() || !fd.deliveryDetails.address.trim() || fd.deliveryDetails.address.length < 10) return false;
      const phoneOk = intl
        ? /^\+?[1-9][0-9]{7,14}$/.test(fd.deliveryDetails.phone)
        : /^[0-9]{10}$/.test(fd.deliveryDetails.phone);
      if (!phoneOk) return false;
      if (!intl && !/^[0-9]{6}$/.test(fd.deliveryDetails.pincode)) return false;
      if (!fd.deliveryDetails.city.trim() || !fd.deliveryDetails.state.trim() || !fd.deliveryDetails.country.trim()) return false;
      if (!fd.deliveryDetails.email?.trim()) return false;
      return true;
    })();

    const productsOk = fd.products.length > 0 && fd.products.every(p =>
      p.description.trim() &&
      p.hsnCode?.trim() &&
      (!intl || /^[0-9]{8,10}$/.test(p.hsnCode.trim())) &&
      p.qty && p.qty >= 1 &&
      parseFloat(p.unitRate) > 0 &&
      parseFloat(p.pieceWt) > 0
    );

    let complete = false;
    if (section === 'pickup') {
      complete = pickupCoreOk;
      if (complete && intl && fd.packageDetails.shipmentType !== 2) {
        complete = !!(fd.pickupDetails.consigner_gst_applicable && fd.pickupDetails.consigner_tax_payment && fd.pickupDetails.export_type);
        if (complete && fd.pickupDetails.export_type !== '1') {
          complete = !!(fd.pickupDetails.tax_paid && fd.pickupDetails.tax_amount && parseFloat(fd.pickupDetails.tax_amount) >= 0);
        }
      }
    } else if (section === 'delivery') {
      complete = deliveryOk;
    } else if (section === 'product') {
      complete = productsOk;
    }

    if (complete) {
      setCollapsedSections(prev => (prev[section] ? prev : { ...prev, [section]: true }));
    }
  }, []);

  const handleCalculateRate = useCallback(async () => {
    // Skip if user already manually picked a courier
    if (userPickedCourier.current) return;

    const hasPincode = isInternational
      ? (formData.pickupDetails.pincode.trim() && formData.deliveryDetails.pincode.trim())
      : (formData.pickupDetails.pincode.match(/^[0-9]{6}$/) && formData.deliveryDetails.pincode.match(/^[0-9]{6}$/));

    const rateWeight = parseFloat(formData.packageDetails.weight);
    const rateNeedsMinWeight = isInternational && selectedCourier?.provider === 'skart' && (formData.packageDetails.shipmentType === 1 || formData.packageDetails.shipmentType === 4);
    if (
      !hasPincode ||
      !rateWeight ||
      rateWeight <= 0 ||
      (rateNeedsMinWeight && rateWeight < 0.1)
    ) {
      return;
    }

    setCalculatingRate(true);
    setRateError('');

    try {
      const rateData = {
        pickupPincode: formData.pickupDetails.pincode,
        deliveryPincode: formData.deliveryDetails.pincode,
        pickupCountry: formData.pickupDetails.country,
        deliveryCountry: formData.deliveryDetails.country,
        weight: parseFloat(formData.packageDetails.weight),
        length: parseFloat(formData.packageDetails.dimensions.length) || 0,
        width: parseFloat(formData.packageDetails.dimensions.width) || 0,
        height: parseFloat(formData.packageDetails.dimensions.height) || 0,
        declaredValue: parseFloat(formData.packageDetails.declaredValue) || 0,
        currency: formData.packageDetails.currency,
        orderType: shippingMode,
        paymentType
      };

      // Fetch rates from all providers simultaneously for comparison
      const raw = await orderService.calculateAllRates(rateData);

      // Both calls return { success, data: {...} } — unwrap the inner data
      const result = raw?.data ?? raw;

      if (result?.courierOptions?.length) {
            result.courierOptions = result.courierOptions.filter(opt => String(opt.id || opt.courier_id) !== '181' && !(opt.name || '').toLowerCase().includes('widect'));
          }
          setRate(result);
          // Auto-select cheapest courier
          if (result?.courierOptions?.length) {
            setSelectedCourierId(String(result.courierOptions[0].id));
          }
    } catch (err) {
      const apiMsg =
        err.response?.data?.message ||
        err.message ||
        'Unable to load shipping rates. Please try again.';
      setRateError(apiMsg);
      setRate(null);
    } finally {
      setCalculatingRate(false);
    }
  }, [
    formData.pickupDetails.pincode,
    formData.deliveryDetails.pincode,
    formData.pickupDetails.country,
    formData.deliveryDetails.country,
    formData.packageDetails.weight,
    formData.packageDetails.dimensions,
    formData.packageDetails.declaredValue,
    shippingMode,
    isInternational,
    isDomestic,
    paymentType
  ]);

  const selectedCourier = useMemo(() => {
    if (!rate?.courierOptions?.length || !selectedCourierId) return null;
    return rate.courierOptions.find(c => String(c.id) === String(selectedCourierId));
  }, [rate, selectedCourierId]);

  // Clear Delhivery Intl details when switching to non-Delhivery courier
  useEffect(() => {
    if (selectedCourier && selectedCourier.provider !== 'delhivery_international') {
      setDelhiveryIntlDetails({ shipmentCategory: null, shipmentSubType: null });
      setBankHint('');
    }
  }, [selectedCourier]);

  const effectiveOrderTotal = useMemo(() => {
    if (selectedCourier) return selectedCourier.totalCharges;
    return rate?.totalAmount ?? 0;
  }, [selectedCourier, rate]);

  const gstBreakdown = useMemo(() => {
    if (selectedCourier?.detailedCharges && Array.isArray(selectedCourier.detailedCharges)) {
      const gstLine = selectedCourier.detailedCharges.find(c => /gst/i.test(String(c.charge_name || '')));
      return {
        label: gstLine?.charge_name || 'GST',
        amount: Number(gstLine?.charge_amount || 0)
      };
    }
    return {
      label: 'GST (18%)',
      amount: Number(selectedCourier?.gst || (selectedCourier?.totalCharges ? selectedCourier.totalCharges - selectedCourier.freightCharges : rate?.gst || 0))
    };
  }, [selectedCourier, rate]);

  useEffect(() => {
    if (!rate?.courierOptions?.length) {
      setSelectedCourierId(null);
      userPickedCourier.current = false;
      return;
    }
    // Only auto-select cheapest if user hasn't manually picked a courier
    if (userPickedCourier.current) return;
    setSelectedCourierId(String(rate.courierOptions[0].id));
  }, [rate]);

  const handleCreateOrder = async () => {
    if (!validateForm()) return;

    // PAN / Aadhaar / IEC are mandatory before placing an order
    try {
      const docRes = await api.get('/kyc/documents');
      const docs = (docRes?.data?.data || []).map(d => String(d.type || '').toLowerCase());
      const kycType = String(user?.kycData?.documentType || '').toLowerCase();
      const hasDoc = (keys) => docs.some(t => keys.some(k => t.includes(k))) || keys.includes(kycType);
      const missing = [];
      if (!hasDoc(['pan card', 'pan allotment letter', 'pan'])) missing.push('PAN');
      if (!hasDoc(['aadhaar card', 'aadhaar', 'aadhar'])) missing.push('Aadhaar');
      if (!hasDoc(['iec copy', 'iec', 'import export code'])) missing.push('IEC');
      if (missing.length) {
        setKycModal({ open: true, missing, message: '' });
        return;
      }
    } catch (err) {
      // Backend enforces this rule too — continue so a network hiccup doesn't block ordering
    }

    if (!rate || !rate.courierOptions?.length) {
      setRateError('Please wait for shipping rates to load and select a courier.');
      return;
    }
    if (!selectedCourierId) {
      setRateError('Please select a courier option.');
      return;
    }

    // Delhivery International requires the user's bank account (AD code / IFSC / AC no.)
    const selectedProvider = rate?.courierOptions?.find(c => c.id === selectedCourierId)?.provider;
    if (selectedProvider === 'delhivery_international') {
      let bank = userBankAccount;
      if (bank === undefined) {
        try {
          const res = await api.get('/bank-account');
          bank = res.data?.data || null;
          setUserBankAccount(bank);
        } catch (err) {
          bank = null;
          setUserBankAccount(null);
        }
      }
      const bankComplete = bank && bank.accountHolderName && bank.accountNumber && bank.ifscCode && bank.adCode;
      if (!bankComplete) {
        setBankHint('Bank account details are required to book Delhivery International shipments. Please add them to continue.');
        setBankModalOpen(true);
        return;
      }
      setBankHint('');
    }

    if (balance < effectiveOrderTotal) {
      setShowRechargeModal(true);
      return;
    }

    try {
      const orderData = {
        orderType: shippingMode,
        pickupDetails: formData.pickupDetails,
        packageDetails: {
          weight: parseFloat(formData.packageDetails.weight),
          dimensions: {
            length: parseFloat(formData.packageDetails.dimensions.length) || 0,
            width: parseFloat(formData.packageDetails.dimensions.width) || 0,
            height: parseFloat(formData.packageDetails.dimensions.height) || 0
          },
          declaredValue: parseFloat(formData.packageDetails.declaredValue) || 0,
          currency: formData.packageDetails.currency,
          packageType: formData.packageDetails.packageType || 'Box',
          shipmentType: formData.packageDetails.shipmentType || 1,
          description: formData.packageDetails.description || 'General Merchandise',
          cargoType: formData.packageDetails.cargoType,
          clearanceType: formData.packageDetails.clearanceType,
          incoterm: formData.packageDetails.incoterm,
          // Delhivery International fields
          invoiceNumber: delhiveryIntlDetails.invoiceNumber || formData.deliveryDetails.invoiceNumber,
          invoiceDate: delhiveryIntlDetails.invoiceDate || formData.deliveryDetails.booking_invoice_date,
          igstPaymentStatus: delhiveryIntlDetails.igstPaymentStatus,
          lutBondRef: delhiveryIntlDetails.lutBondRef,
          ecommerceExport: delhiveryIntlDetails.ecommerceExport,
          delhiveryIncoterm: delhiveryIntlDetails.incoterm || '',
          // Delhivery shipment type from category picker (independent of service code)
          delhiveryShipmentType: (() => {
            const c = delhiveryIntlDetails.shipmentCategory;
            const s = delhiveryIntlDetails.shipmentSubType;
            if (c === 'non_commercial' && s === 'document') return 'document';
            if (c === 'non_commercial' && s === 'sample') return 'sample';
            if (c === 'non_commercial' && s === 'gift') return 'gift';
            if (c === 'commercial' && s === 'cargo') return 'commercial_cargo';
            if (c === 'commercial' && s === 'csb5') return 'commercial';
            return '';
          })(),
          insuranceValue: delhiveryIntlDetails.insuranceValue,
          freightValue: delhiveryIntlDetails.freightValue,
        },
        // Delhivery International — override delivery details with IOR if set
        deliveryDetails: {
          ...formData.deliveryDetails,
          ...(delhiveryIntlDetails.hasIOR && delhiveryIntlDetails.iorName ? {
            invoiceNumber: delhiveryIntlDetails.invoiceNumber || formData.deliveryDetails.invoiceNumber,
            booking_invoice_date: delhiveryIntlDetails.invoiceDate || formData.deliveryDetails.booking_invoice_date,
          } : {}),
        },
        products: formData.products.map(p => ({
          ...p,
          unitRate: parseFloat(p.unitRate),
          pieceWt: parseFloat(p.pieceWt),
          qty: parseInt(p.qty)
        })),
        shipmentType: formData.packageDetails.shipmentType || 1,
        // Composite ID — backend decodes provider automatically
        nimbusCourierId: selectedCourierId,
        clientId: selectedClientId || null,
        // ✅ Explicitly set deliveryPartner for backend validation
        deliveryPartner: rate?.courierOptions?.find(c => c.id === selectedCourierId)?.provider || 'nimbuspost',
        paymentType,
        originalOrderId: reverseOriginalId || undefined
      };

      const result = await createOrder(orderData);
      // Note: the draft is intentionally kept so a failed pickup can be
      // re-attempted without re-typing. Cleared via "Start fresh" instead.
      navigate(`/order-details?id=${result.order.id}`);
    } catch (err) {
      console.error('Order creation error:', err);
      if (err?.response?.data?.message?.includes('You have not uploaded')) {
        setKycModal({ open: true, missing: [], message: err.response.data.message });
      } else if (err?.response?.data?.errors?.length) {
        // Backend field-by-field validation — show each message under its own field
        const fieldErrors = {};
        err.response.data.errors.forEach(e => {
          if (e?.field) fieldErrors[e.field] = e.message;
        });
        setFormErrors(fieldErrors);
      }
    }
  };

  useEffect(() => {
    // Don't auto-recalculate if user already picked a courier
    if (userPickedCourier.current) return;

    const hasPincode = isInternational
      ? (formData.pickupDetails.pincode.trim() && formData.deliveryDetails.pincode.trim())
      : (formData.pickupDetails.pincode.match(/^[0-9]{6}$/) && formData.deliveryDetails.pincode.match(/^[0-9]{6}$/));

    const hasCountry = formData.pickupDetails.country?.trim() && formData.deliveryDetails.country?.trim();

    const autoWeight = parseFloat(formData.packageDetails.weight);
    const autoNeedsMinWeight = isInternational && selectedCourier?.provider === 'skart' && (formData.packageDetails.shipmentType === 1 || formData.packageDetails.shipmentType === 4);
    if (
      hasPincode &&
      hasCountry &&
      autoWeight &&
      autoWeight > 0 &&
      (!autoNeedsMinWeight || autoWeight >= 0.1)
    ) {
      rateTimerRef.current = setTimeout(() => {
        handleCalculateRate();
      }, 1000);
      return () => clearTimeout(rateTimerRef.current);
    }
  }, [
    formData.pickupDetails.pincode,
    formData.deliveryDetails.pincode,
    formData.pickupDetails.country,
    formData.deliveryDetails.country,
    formData.packageDetails.weight,
    formData.packageDetails.declaredValue,
    shippingMode,
    isInternational,
    paymentType,
    handleCalculateRate
  ]);

  // Auto-fill tax fields when LUT (export_type=1) is selected
  useEffect(() => {
    if (formData.pickupDetails.export_type === '1') {
      setFormData(prev => ({
        ...prev,
        pickupDetails: {
          ...prev.pickupDetails,
          tax_paid: '2',
          tax_amount: '00'
        }
      }));
    }
  }, [formData.pickupDetails.export_type]);

  // GST Applicable = No + Tax Payment = Not Applicable → Tax Paid = No, Tax Amount = 00
  useEffect(() => {
    if (formData.pickupDetails.consigner_gst_applicable === '2' && formData.pickupDetails.consigner_tax_payment === '3') {
      setFormData(prev => ({
        ...prev,
        pickupDetails: {
          ...prev.pickupDetails,
          tax_paid: '2',
          tax_amount: '00'
        }
      }));
    }
  }, [formData.pickupDetails.consigner_gst_applicable, formData.pickupDetails.consigner_tax_payment]);

  // Auto-fill package weight & declared value from product details
  useEffect(() => {
    const totalWeight = formData.products.reduce((sum, p) => sum + (parseFloat(p.pieceWt) || 0), 0);
    const totalDeclaredValue = formData.products.reduce((sum, p) => sum + ((parseFloat(p.qty) || 0) * (parseFloat(p.unitRate) || 0)), 0);

    setFormData(prev => ({
      ...prev,
      packageDetails: {
        ...prev.packageDetails,
        weight: totalWeight || prev.packageDetails.weight,
        declaredValue: totalDeclaredValue || prev.packageDetails.declaredValue
      }
    }));
  }, [formData.products]);

  return (
    <KYCGuard message="Complete KYC verification to create orders and start shipping with us.">
      <div className="min-h-screen mb-24" style={{ pointerEvents: 'auto' }}>
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <button onClick={() => navigate(-1)} className="cursor-pointer">
            <ArrowLeft className="w-6 h-6 text-gray-600" />
          </button>
          <h1 className="text-xl font-bold text-[#131842]">Create Order</h1>
        </div>

        {/* Restored draft notice */}
        {draftRestored && (
          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-center justify-between gap-3 text-sm">
            <span className="text-blue-700">Form restored from your previous order — fix anything and create the order again.</span>
            <button
              onClick={startFreshForm}
              className="px-3 py-1 text-xs font-semibold text-blue-700 bg-white border border-blue-200 rounded hover:bg-blue-100 whitespace-nowrap"
            >
              Start fresh
            </button>
          </div>
        )}

        {/* Error Message */}
        {(error || rateError) && (
          <div className="mb-6 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-600 text-sm">
            <AlertCircle size={16} />
            <span>{sanitizeError(error || rateError)}</span>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-[3fr_2fr] gap-6">
          {/* LEFT COLUMN */}
          <div className="space-y-4">
            {/* Pickup Details */}
            <div className="bg-white border rounded-lg p-4 space-y-4 shadow-md">
              <h3 className="font-semibold flex items-center justify-between text-sm">
                <div className="flex items-center gap-2">
                  <span>📍</span> Pickup Details
                </div>
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Client:</span>
                    <select
                      value={selectedClientId}
                      onChange={(e) => setSelectedClientId(e.target.value)}
                      className="text-[10px] bg-blue-50 border border-blue-100 rounded px-2 py-1 font-bold text-blue-700 outline-none focus:border-blue-500"
                    >
                      <option value="">Self (No Client)</option>
                      {clients.map(c => (
                        <option key={c._id} value={c._id}>{c.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="flex items-center gap-2">
                    <Warehouse className="w-3.5 h-3.5 text-gray-400" />
                    <select
                      value={selectedWarehouseId}
                      onChange={(e) => {
                        const id = e.target.value;
                        setSelectedWarehouseId(id);
                        const wh = warehouses.find(w => w._id === id);
                        if (wh) handleWarehouseSelect(wh);
                      }}
                      className="text-[10px] bg-gray-50 border border-gray-200 rounded px-2 py-1 font-bold text-gray-700 outline-none focus:border-blue-500"
                    >
                      <option value="">{warehouses.length === 0 ? 'No warehouse yet' : 'Select Warehouse'}</option>
                      {warehouses.map(w => (
                        <option key={w._id} value={w._id}>{w.name}</option>
                      ))}
                    </select>
                  </div>
                  <button
                    onClick={() => toggleSection('pickup')}
                    className="p-1 rounded hover:bg-gray-100 transition-colors"
                    aria-label="Collapse or expand Pickup Details"
                  >
                    <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform duration-200 ${collapsedSections.pickup ? 'rotate-180' : ''}`} />
                  </button>
                </div>
              </h3>

              {!collapsedSections.pickup && (
              <div className="p-3 border border-gray-100 rounded-lg bg-gray-50 space-y-4" onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) maybeCollapseSection('pickup'); }}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <InputField
                  label="Name"
                  value={formData.pickupDetails.name}
                  onChange={(val) => handleChange('pickupDetails', 'name', val)}
                  error={formErrors['pickupDetails.name']}
                  placeholder="Enter pickup name"
                  required
                />
                <InputField
                  label="Phone"
                  type="tel"
                  value={formData.pickupDetails.phone}
                  onChange={(val) => handleChange('pickupDetails', 'phone', val.replace(/\D/g, ''))}
                  error={formErrors['pickupDetails.phone']}
                  placeholder="10-digit mobile number"
                  required
                  maxLength={10}
                />
              </div>

              <InputField
                label="Address"
                value={formData.pickupDetails.address}
                onChange={(val) => handleChange('pickupDetails', 'address', val)}
                error={formErrors['pickupDetails.address']}
                placeholder="Enter complete address"
                required
              />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <InputField
                  label="Country"
                  value={formData.pickupDetails.country}
                  onChange={(val) => handleChange('pickupDetails', 'country', val)}
                  error={formErrors['pickupDetails.country']}
                  placeholder="India"
                  required
                  list={countries}
                  listId="pickup-country-list"
                />
                <InputField
                  label="Company Name (Optional)"
                  value={formData.pickupDetails.companyName}
                  onChange={(val) => handleChange('pickupDetails', 'companyName', val)}
                  error={formErrors['pickupDetails.companyName']}
                  placeholder="Enter company name"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <InputField
                  label="Email"
                  type="email"
                  value={formData.pickupDetails.email}
                  onChange={(val) => handleChange('pickupDetails', 'email', val)}
                  error={formErrors['pickupDetails.email']}
                  placeholder="Enter email address"
                  required
                />
                <InputField
                  label="Pincode"
                  type="text"
                  value={formData.pickupDetails.pincode}
                  onChange={(val) => handleChange('pickupDetails', 'pincode', val.replace(/\D/g, ''))}
                  error={formErrors['pickupDetails.pincode']}
                  placeholder="Pincode"
                  required
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <InputField
                  label="City"
                  value={formData.pickupDetails.city}
                  onChange={(val) => handleChange('pickupDetails', 'city', val)}
                  error={formErrors['pickupDetails.city']}
                  placeholder="Enter city"
                  required
                />
                <InputField
                  label="State"
                  value={formData.pickupDetails.state}
                  onChange={(val) => handleChange('pickupDetails', 'state', val)}
                  error={formErrors['pickupDetails.state']}
                  placeholder="Enter state"
                  required
                  onBlur={() => maybeCollapseSection('pickup')}
                />
              </div>

              {/* GST & Export Compliance — International only (hidden for Document shipments) */}
              {isInternational && formData.packageDetails.shipmentType !== 2 && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                <div className="space-y-1">
                  <label className="text-xs font-medium text-gray-700">
                    Consigner GST Applicable <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={formData.pickupDetails.consigner_gst_applicable}
                    onChange={(e) => handleGstApplicableChange(e.target.value)}
                    className={`w-full border rounded-md p-2 text-sm focus:outline-none focus:ring-2 focus:border-blue-500 ${formErrors['pickupDetails.consigner_gst_applicable'] ? 'border-red-500 focus:ring-red-200' : 'border-gray-300 focus:ring-blue-200'}`}
                  >
                    <option value="">Select</option>
                    <option value="1">Yes</option>
                    <option value="2">No</option>
                  </select>
                  {formErrors['pickupDetails.consigner_gst_applicable'] && (
                    <p className="text-xs text-red-500 mt-1">{formErrors['pickupDetails.consigner_gst_applicable']}</p>
                  )}
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-medium text-gray-700">
                    Consigner Tax Payment <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={formData.pickupDetails.consigner_tax_payment}
                    onChange={(e) => {
                      handleChange('pickupDetails', 'consigner_tax_payment', e.target.value);
                      setFormErrors(prev => {
                        const next = { ...prev };
                        delete next['pickupDetails.consigner_tax_payment'];
                        return next;
                      });
                    }}
                    className={`w-full border rounded-md p-2 text-sm focus:outline-none focus:ring-2 focus:border-blue-500 ${formErrors['pickupDetails.consigner_tax_payment'] ? 'border-red-500 focus:ring-red-200' : 'border-gray-300 focus:ring-blue-200'}`}
                  >
                    <option value="">Select</option>
                    <option value="1">IGST Payment</option>
                    <option value="2">Against Bond or Undertaking</option>
                    <option value="3">Not Applicable</option>
                  </select>
                  {formErrors['pickupDetails.consigner_tax_payment'] && (
                    <p className="text-xs text-red-500 mt-1">{formErrors['pickupDetails.consigner_tax_payment']}</p>
                  )}
                </div>
              </div>
              )}


              {isInternational && formData.packageDetails.shipmentType !== 2 && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-1">
                    <label className="text-xs font-medium text-gray-700">
                      Export Type <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={formData.pickupDetails.export_type}
                      onChange={(e) => {
                        handleChange('pickupDetails', 'export_type', e.target.value);
                        setFormErrors(prev => {
                          const next = { ...prev };
                          delete next['pickupDetails.export_type'];
                          return next;
                        });
                      }}
                      className={`w-full border rounded-md p-2 text-sm focus:outline-none focus:ring-2 focus:border-blue-500 ${formErrors['pickupDetails.export_type'] ? 'border-red-500 focus:ring-red-200' : 'border-gray-300 focus:ring-blue-200'}`}
                    >
                      <option value="">Select</option>
                      <option value="1">LUT</option>
                      <option value="2">Bond</option>
                      <option value="3">Not Applicable</option>
                    </select>
                    {formErrors['pickupDetails.export_type'] && (
                      <p className="text-xs text-red-500 mt-1">{formErrors['pickupDetails.export_type']}</p>
                    )}
                  </div>
                  <div className="space-y-1">
                    <label className="text-xs font-medium text-gray-700">
                      Tax Paid <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={formData.pickupDetails.tax_paid}
                      onChange={(e) => {
                        handleChange('pickupDetails', 'tax_paid', e.target.value);
                        setFormErrors(prev => {
                          const next = { ...prev };
                          delete next['pickupDetails.tax_paid'];
                          return next;
                        });
                      }}
                      disabled={formData.pickupDetails.export_type === '1'}
                      className={`w-full border rounded-md p-2 text-sm focus:outline-none focus:ring-2 focus:border-blue-500 ${formErrors['pickupDetails.tax_paid'] ? 'border-red-500 focus:ring-red-200' : 'border-gray-300 focus:ring-blue-200'}`}
                    >
                      {formData.pickupDetails.export_type === '1' ? (
                        <option value="2">No</option>
                      ) : (
                        <>
                          <option value="">Select</option>
                          <option value="1">Yes</option>
                          <option value="2">No</option>
                        </>
                      )}
                    </select>
                    {formErrors['pickupDetails.tax_paid'] && (
                      <p className="text-xs text-red-500 mt-1">{formErrors['pickupDetails.tax_paid']}</p>
                    )}
                  </div>
                  <div className="space-y-1">
                    <label className="text-xs font-medium text-gray-700">
                      Tax Amount <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      value={formData.pickupDetails.tax_amount}
                      onChange={(e) => {
                        handleChange('pickupDetails', 'tax_amount', e.target.value);
                        setFormErrors(prev => {
                          const next = { ...prev };
                          delete next['pickupDetails.tax_amount'];
                          return next;
                        });
                      }}
                      disabled={formData.pickupDetails.export_type === '1'}
                      min="0"
                      step="0.01"
                      placeholder="0.00"
                      onBlur={() => maybeCollapseSection('pickup')}
                      className={`w-full border rounded-md p-2 text-sm focus:outline-none focus:ring-2 focus:border-blue-500 ${formErrors['pickupDetails.tax_amount'] ? 'border-red-500 focus:ring-red-200' : 'border-gray-300 focus:ring-blue-200'}`}
                      style={{ pointerEvents: 'auto', zIndex: 1 }}
                    />
                    {formErrors['pickupDetails.tax_amount'] && (
                      <p className="text-xs text-red-500 mt-1">{formErrors['pickupDetails.tax_amount']}</p>
                    )}
                  </div>
                </div>
              )}
              </div>
              )}
            </div>

            {/* Delivery Details */}
            <div className="bg-white border rounded-lg p-4 space-y-4 shadow-md">
              <h3 className="font-semibold flex items-center justify-between text-sm">
                <div className="flex items-center gap-2">
                  <span>🚚</span> Delivery Details
                </div>
                <button
                  onClick={() => toggleSection('delivery')}
                  className="p-1 rounded hover:bg-gray-100 transition-colors"
                  aria-label="Collapse or expand Delivery Details"
                >
                  <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform duration-200 ${collapsedSections.delivery ? 'rotate-180' : ''}`} />
                </button>
              </h3>

              {!collapsedSections.delivery && (
              <div className="p-3 border border-gray-100 rounded-lg bg-gray-50 space-y-4" onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) maybeCollapseSection('delivery'); }}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <InputField
                  label="Name"
                  value={formData.deliveryDetails.name}
                  onChange={(val) => handleChange('deliveryDetails', 'name', val)}
                  error={formErrors['deliveryDetails.name']}
                  placeholder="Enter delivery name"
                  required
                />
                <InputField
                  label="Phone"
                  type="tel"
                  value={formData.deliveryDetails.phone}
                  onChange={(val) => handleChange('deliveryDetails', 'phone',
                    isInternational
                      ? val.replace(/[^\d+]/g, '').replace(/(?!^)\+/g, '')
                      : val.replace(/\D/g, ''))}
                  error={formErrors['deliveryDetails.phone']}
                  placeholder={isInternational ? "e.g. +14155550123" : "10-digit mobile number"}
                  required
                  international={isInternational}
                  maxLength={16}
                />
              </div>

              <InputField
                label="Address"
                value={formData.deliveryDetails.address}
                onChange={(val) => handleChange('deliveryDetails', 'address', val)}
                error={formErrors['deliveryDetails.address']}
                placeholder="Enter complete address"
                required
              />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <InputField
                  label="Country"
                  value={formData.deliveryDetails.country}
                  onChange={(val) => handleChange('deliveryDetails', 'country', val)}
                  error={formErrors['deliveryDetails.country']}
                  placeholder="e.g. USA, UK"
                  required
                  list={countries}
                  listId="delivery-country-list"
                />
                <InputField
                  label="Company Name (Optional)"
                  value={formData.deliveryDetails.companyName}
                  onChange={(val) => handleChange('deliveryDetails', 'companyName', val)}
                  error={formErrors['deliveryDetails.companyName']}
                  placeholder="Enter company name"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <InputField
                  label="Email"
                  type="email"
                  value={formData.deliveryDetails.email}
                  onChange={(val) => handleChange('deliveryDetails', 'email', val)}
                  error={formErrors['deliveryDetails.email']}
                  placeholder="Enter email address"
                  required
                />
                <InputField
                  label="Pincode"
                  type="text"
                  value={formData.deliveryDetails.pincode}
                  onChange={(val) => handleChange('deliveryDetails', 'pincode', val)}
                  error={formErrors['deliveryDetails.pincode']}
                  placeholder="Pincode"
                  required
                  international={isInternational}
                />
              </div>
              {isInternational && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    label="VAT ID (UK Only)"
                    type="text"
                    value={formData.deliveryDetails.vatId}
                    onChange={(val) => handleChange('deliveryDetails', 'vatId', val)}
                    error={formErrors['deliveryDetails.vatId']}
                    placeholder="Required for UK DDU shipments"
                  />
                  <InputField
                    label="IOSS Number (Europe)"
                    type="text"
                    value={formData.deliveryDetails.iossNumber}
                    onChange={(val) => handleChange('deliveryDetails', 'iossNumber', val)}
                    error={formErrors['deliveryDetails.iossNumber']}
                    placeholder="Required for Europe DDU shipments"
                  />
                </div>
              )}



              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <InputField
                  label="City"
                  value={formData.deliveryDetails.city}
                  onChange={(val) => handleChange('deliveryDetails', 'city', val)}
                  error={formErrors['deliveryDetails.city']}
                  placeholder="Enter city"
                  required
                />
                <InputField
                  label="State"
                  value={formData.deliveryDetails.state}
                  onChange={(val) => handleChange('deliveryDetails', 'state', val)}
                  error={formErrors['deliveryDetails.state']}
                  placeholder="Enter state"
                  required
                  onBlur={!isInternational ? () => maybeCollapseSection('delivery') : undefined}
                />
              </div>

              {isInternational && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                <div className="space-y-1">
                  <label className="text-xs font-medium text-gray-700">
                    Booking Invoice Date
                  </label>
                  <input
                    type="date"
                    value={formData.deliveryDetails.booking_invoice_date}
                    onChange={(e) => handleChange('deliveryDetails', 'booking_invoice_date', e.target.value)}
                    className="w-full border rounded-md p-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-500 border-gray-300"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-medium text-gray-700">
                    Invoice Number
                  </label>
                  <input
                    type="text"
                    value={formData.deliveryDetails.invoiceNumber}
                    onChange={(e) => handleChange('deliveryDetails', 'invoiceNumber', e.target.value)}
                    placeholder="Enter invoice number"
                    onBlur={() => maybeCollapseSection('delivery')}
                    className="w-full border rounded-md p-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-500 border-gray-300"
                  />
                </div>
              </div>
              )}
              </div>
              )}
            </div>

            {/* ❌ Delivery Partner section REMOVED */}
            {/* Rates now fetched from all providers automatically */}

            {/* Product Details */}
            <div className="bg-white border rounded-lg p-4 space-y-4 shadow-md">
              <div className="flex justify-between items-center">
                <h3 className="font-semibold flex items-center gap-2 text-sm">
                  <span>🛍️</span> Product Details
                </h3>
                <div className="flex items-center gap-3">
                  <button
                    onClick={addProduct}
                    className="text-xs text-blue-600 font-semibold hover:underline"
                  >
                    + Add Product
                  </button>
                  <button
                    onClick={() => toggleSection('product')}
                    className="p-1 rounded hover:bg-gray-100 transition-colors"
                    aria-label="Collapse or expand Product Details"
                  >
                    <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform duration-200 ${collapsedSections.product ? 'rotate-180' : ''}`} />
                  </button>
                </div>
              </div>

              {!collapsedSections.product && (
              <div className="space-y-6" onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) maybeCollapseSection('product'); }}>
                {formData.products.map((product, index) => (
                  <div key={index} className="p-3 border border-gray-100 rounded-lg bg-gray-50 relative space-y-3">
                    {formData.products.length > 1 && (
                      <button
                        onClick={() => removeProduct(index)}
                        className="absolute top-2 right-2 text-red-500 hover:text-red-700 p-1"
                      >
                        <AlertCircle size={14} />
                      </button>
                    )}

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <InputField
                        label="Description"
                        value={product.description}
                        onChange={(val) => handleProductChange(index, 'description', val)}
                        error={formErrors[`products.${index}.description`]}
                        placeholder="What's in the box?"
                        required
                      />
                      <InputField
                        label="HSN Code"
                        value={product.hsnCode}
                        onChange={(val) => handleProductChange(index, 'hsnCode', val)}
                        error={formErrors[`products.${index}.hsnCode`]}
                        placeholder="8-digit HSN"
                        required
                      />
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      <InputField
                        label="Qty"
                        type="number"
                        value={product.qty}
                        onChange={(val) => handleProductChange(index, 'qty', val)}
                        error={formErrors[`products.${index}.qty`]}
                        min="1"
                        required
                      />
                      <InputField
                        label="Unit Price (₹)"
                        type="number"
                        value={product.unitRate}
                        onChange={(val) => handleProductChange(index, 'unitRate', val)}
                        error={formErrors[`products.${index}.unitRate`]}
                        placeholder="0.00"
                        required
                      />
                      <InputField
                        label={`Weight (${weightUnit})`}
                        type="number"
                        value={weightUnit === 'g' ? (product.pieceWt ? Math.round(parseFloat(product.pieceWt) * 1000) : '') : product.pieceWt}
                        onChange={(val) => handleProductChange(index, 'pieceWt', weightUnit === 'g' ? (val ? String(parseFloat(val) / 1000) : '') : val)}
                        error={formErrors[`products.${index}.pieceWt`]}
                        placeholder={weightUnit === 'g' ? '100' : '0.1'}
                        required
                        step={weightUnit === 'g' ? 1 : 0.1}
                        unit={weightUnit}
                        onUnitChange={setWeightUnit}
                        onBlur={() => maybeCollapseSection('product')}
                      />
                    </div>
                  </div>
                ))}
              </div>
              )}
            </div>
          </div>

          {/* RIGHT COLUMN */}
          <div className="space-y-4">
            {/* Package Details */}
            <div className="bg-white border rounded-lg p-4 space-y-4 shadow-md">
              <h3 className="font-semibold flex items-center gap-2 text-sm">
                <span>📦</span> Package Details
              </h3>

              <div className="p-3 border border-gray-100 rounded-lg bg-gray-50 space-y-4">
              <div className="space-y-1">
                <label className="text-xs font-medium text-gray-700">Package Type</label>
                <select
                  value={formData.packageDetails.packageType}
                  onChange={(e) => handlePackageChange('packageType', e.target.value)}
                  className="w-full border border-gray-300 rounded-md p-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-500"
                >
                  <option value="Box">Box / Parcel</option>
                  <option value="Envelope">Envelope / Document</option>
                </select>
              </div>

              {/* Payment Mode — Domestic only */}
              {!isInternational && (
              <div className="space-y-1">
                <label className="text-xs font-medium text-gray-700">Payment Mode</label>
                <select
                  value={paymentType}
                  onChange={(e) => setPaymentType(e.target.value)}
                  className="w-full border border-gray-300 rounded-md p-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-500"
                >
                  <option value="prepaid">Prepaid</option>
                  <option value="cod">COD</option>
                </select>
              </div>
              )}

              {/* Shipment Type — International only (domestic always uses Non-Doc) */}
              {isInternational && (
              <div className="space-y-1">
                <label className="text-xs font-medium text-gray-700">Shipment Type</label>
                <select
                  value={formData.packageDetails.shipmentType}
                  onChange={(e) => {
                    const newValue = parseInt(e.target.value);
                    handlePackageChange('shipmentType', newValue);
                    if (newValue !== 4) {
                      setFormData(prev => ({
                        ...prev,
                        packageDetails: { ...prev.packageDetails, cargoType: '', clearanceType: '', incoterm: '' }
                      }));
                      setFormErrors(prev => {
                        const next = { ...prev };
                        delete next['packageDetails.cargoType'];
                        delete next['packageDetails.clearanceType'];
                        delete next['packageDetails.incoterm'];
                        return next;
                      });
                    }
                    if (newValue === 2) {
                      // Document shipment: GST / tax fields are hidden — prefill No / Not Applicable
                      setFormData(prev => ({
                        ...prev,
                        pickupDetails: {
                          ...prev.pickupDetails,
                          consigner_gst_applicable: '2',
                          consigner_tax_payment: '3',
                          export_type: '3',
                          tax_paid: '2',
                          tax_amount: '00'
                        }
                      }));
                      setFormErrors(prev => {
                        const next = { ...prev };
                        delete next['pickupDetails.consigner_gst_applicable'];
                        delete next['pickupDetails.consigner_tax_payment'];
                        delete next['pickupDetails.export_type'];
                        delete next['pickupDetails.tax_paid'];
                        delete next['pickupDetails.tax_amount'];
                        return next;
                      });
                    }
                  }}
                  className="w-full border border-gray-300 rounded-md p-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-500"
                >
                  <option value={1}>Non-Doc (Goods)</option>
                  <option value={2}>Doc (Documents Only)</option>
                  <option value={4}>Commercial</option>
                </select>
              </div>
              )}

              {/* International Customs Fields */}
              {isInternational && formData.packageDetails.shipmentType === 4 && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-1">
                    <label className="text-xs font-medium text-gray-700">
                      Cargo Type <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={formData.packageDetails.cargoType}
                      onChange={(e) => handleCargoTypeChange(e.target.value)}
                      className={`w-full border rounded-md p-2 text-sm focus:outline-none focus:ring-2 focus:border-blue-500 ${formErrors['packageDetails.cargoType'] ? 'border-red-500 focus:ring-red-200' : 'border-gray-300 focus:ring-blue-200'}`}
                    >
                      <option value="">Select Cargo Type</option>
                      <option value="CSB5">CSB5</option>
                      <option value="CARGO">CARGO</option>
                    </select>
                    {formErrors['packageDetails.cargoType'] && (
                      <p className="text-xs text-red-500 mt-1">{formErrors['packageDetails.cargoType']}</p>
                    )}
                  </div>
                  <div className="space-y-1">
                    <label className="text-xs font-medium text-gray-700">
                      Clearance Type <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={formData.packageDetails.clearanceType}
                      onChange={(e) => {
                        handlePackageChange('clearanceType', e.target.value);
                        setFormData(prev => ({ ...prev, packageDetails: { ...prev.packageDetails, incoterm: '' } }));
                      }}
                      disabled={formData.packageDetails.cargoType === 'CSB5'}
                      className={`w-full border rounded-md p-2 text-sm focus:outline-none focus:ring-2 focus:border-blue-500 ${formErrors['packageDetails.clearanceType'] ? 'border-red-500 focus:ring-red-200' : 'border-gray-300 focus:ring-blue-200'} disabled:bg-gray-100 disabled:cursor-not-allowed`}
                    >
                      <option value="">Select Clearance Type</option>
                      {formData.packageDetails.cargoType === 'CSB5' ? (
                        <option value="INTEGRATOR">INTEGRATOR</option>
                      ) : (
                        <>
                          <option value="SKART">SKART</option>
                          <option value="SELF">SELF</option>
                          <option value="INTEGRATOR">INTEGRATOR</option>
                        </>
                      )}
                    </select>
                    {formErrors['packageDetails.clearanceType'] && (
                      <p className="text-xs text-red-500 mt-1">{formErrors['packageDetails.clearanceType']}</p>
                    )}
                  </div>
                  <div className="space-y-1">
                    <label className="text-xs font-medium text-gray-700">
                      Incoterm <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={formData.packageDetails.incoterm}
                      onChange={(e) => handlePackageChange('incoterm', e.target.value)}
                      className={`w-full border rounded-md p-2 text-sm focus:outline-none focus:ring-2 focus:border-blue-500 ${formErrors['packageDetails.incoterm'] ? 'border-red-500 focus:ring-red-200' : 'border-gray-300 focus:ring-blue-200'}`}
                    >
                      <option value="">Select Incoterm</option>
                      <option value="DAP_WITHOUT">DAP (Without Destination Clearance)</option>
                      <option value="DAP_WITH">DAP (With Destination Clearance)</option>
                      <option value="FOB_BSO">FOB/BSO</option>
                      <option value="DDP">DDP</option>
                    </select>
                    {formErrors['packageDetails.incoterm'] && (
                      <p className="text-xs text-red-500 mt-1">{formErrors['packageDetails.incoterm']}</p>
                    )}
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
                <InputField
                  label="Declared Value (Total)"
                  type="number"
                  value={formData.packageDetails.declaredValue}
                  onChange={(val) => handlePackageChange('declaredValue', val)}
                  error={formErrors['packageDetails.declaredValue']}
                  placeholder="Total invoice value"
                  required
                />
                <div className="space-y-1">
                  <label className="text-xs font-medium text-gray-700">
                    Currency <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={formData.packageDetails.currency}
                    onChange={(e) => handlePackageChange('currency', e.target.value)}
                    className="w-full border border-gray-300 rounded-md p-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-500"
                  >
                    <option value="">Select Currency</option>
                    {currencies.map((curr, idx) => {
                      const id = curr.currency_id || curr.id || (typeof curr === 'string' ? curr : idx);
                      const name = curr.currency_name || curr.currency || curr.name || (typeof curr === 'string' ? curr : 'Unknown');
                      const code = curr.currency_code || curr.code || '';
                      return (
                        <option key={idx} value={id}>
                          {name} {code ? `(${code})` : ''}
                        </option>
                      );
                    })}
                    {/* Comprehensive Fallback List */}
                    {currencies.length === 0 && (
                        <>
                            <option value="24">Indian Rupee (INR)</option>
                            <option value="48">US Dollar (USD)</option>
                            <option value="22">Euro (EUR)</option>
                            <option value="50">British Pound (GBP)</option>
                            <option value="13">Australian Dollar (AUD)</option>
                            <option value="31">Canadian Dollar (CAD)</option>
                            <option value="155">Singapore Dollar (SGD)</option>
                            <option value="225">United Arab Emirates Dirham (AED)</option>
                        </>
                    )}
                  </select>
                </div>
              </div>

              <InputField
                label={`Weight (${weightUnit})`}
                type="number"
                value={weightUnit === 'g' ? (formData.packageDetails.weight ? Math.round(parseFloat(formData.packageDetails.weight) * 1000) : '') : formData.packageDetails.weight}
                onChange={(val) => handlePackageChange('weight', weightUnit === 'g' ? (val ? String(parseFloat(val) / 1000) : '') : val)}
                error={formErrors['packageDetails.weight']}
                placeholder={weightUnit === 'g' ? '100' : '0.1'}
                required
                min={weightUnit === 'g' ? 1 : 0.01}
                step={weightUnit === 'g' ? 1 : 0.1}
                unit={weightUnit}
                onUnitChange={setWeightUnit}
              />

              <div className="space-y-1">
                <label className="text-xs font-medium text-gray-700">Dimensions (cm) <span className="text-red-500">*</span></label>
                <div className="flex gap-2">
                  <input
                    type="number"
                    value={formData.packageDetails.dimensions.length || ''}
                    onChange={(e) => handlePackageChange('dimensions.length', e.target.value)}
                    placeholder="L"
                    min="0.1"
                    step="0.1"
                    required
                    className="w-full border border-gray-300 rounded-md p-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-500"
                    style={{ pointerEvents: 'auto', zIndex: 1 }}
                  />
                  <input
                    type="number"
                    value={formData.packageDetails.dimensions.width || ''}
                    onChange={(e) => handlePackageChange('dimensions.width', e.target.value)}
                    placeholder="W"
                    min="0.1"
                    step="0.1"
                    required
                    className="w-full border border-gray-300 rounded-md p-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-500"
                    style={{ pointerEvents: 'auto', zIndex: 1 }}
                  />
                  <input
                    type="number"
                    value={formData.packageDetails.dimensions.height || ''}
                    onChange={(e) => handlePackageChange('dimensions.height', e.target.value)}
                    placeholder="H"
                    min="0.1"
                    step="0.1"
                    required
                    className="w-full border border-gray-300 rounded-md p-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-500"
                    style={{ pointerEvents: 'auto', zIndex: 1 }}
                  />
                </div>
              </div>
              </div>

            </div>

            {/* Rate Calculation & Unified Courier Selection */}
            {rate && (
              <div className="bg-white border rounded-lg p-4 space-y-4">
                <h3 className="font-semibold flex items-center gap-2 text-sm">
                  <span>💰</span> Shipping Rate
                </h3>

                {/* Payment type — International only (domestic uses Payment Mode in Package Details) */}
                {/* COD removed for international — only prepaid is supported */}
                {isInternational && (
                <div className="space-y-1">
                  <label className="text-xs font-medium text-gray-700">Payment type</label>
                  <select
                    value={paymentType}
                    onChange={e => setPaymentType(e.target.value)}
                    className="w-full border border-gray-300 rounded-md p-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-200"
                  >
                    <option value="prepaid">Prepaid</option>
                  </select>
                </div>
                )}

                {rate.courierOptions?.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-xs font-medium text-gray-700">Select courier</p>
                    <div className="space-y-2 max-h-56 overflow-y-auto">
                      {rate.courierOptions.map(opt => (
                        <label
                          key={String(opt.id)}
                          className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer text-sm ${String(selectedCourierId) === String(opt.id)
                              ? 'border-blue-500 bg-blue-50'
                              : 'border-gray-200 hover:border-gray-300'
                            }`}
                        >
                          <input
                            type="radio"
                            name="courier"
                            className="mt-1"
                            checked={String(selectedCourierId) === String(opt.id)}
                            onChange={() => { clearTimeout(rateTimerRef.current); setSelectedCourierId(String(opt.id)); userPickedCourier.current = true; }}
                          />
                          <span className="flex-1">
                            <span className="font-medium text-gray-900 block">{opt.name}</span>
                            <span className="text-xs text-gray-500">
                              Freight ₹{Number(opt.freightCharges).toFixed(2)}
                              {(opt.codCharges || 0) > 0 && (
                                <> · COD ₹{Number(opt.codCharges || 0).toFixed(2)}</>
                              )}
                              {opt.estimatedDelivery && (
                                <> · {opt.estimatedDelivery}</>
                              )}
                            </span>
                          </span>
                          <span className="font-semibold text-blue-600 whitespace-nowrap flex items-center gap-1.5">
                            <button
                              type="button"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setBreakdownCourier(opt);
                              }}
                              className="inline-flex items-center text-blue-500 hover:text-blue-700 transition-colors cursor-pointer"
                              title="View price breakdown"
                            >
                              <Info size={15} />
                            </button>
                            ₹{Number(opt.totalCharges).toFixed(2)}
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}

                {rate.courierOptions?.length === 0 && (
                  <p className="text-sm text-amber-600">No couriers available for this route. Try changing the pincode or weight.</p>
                )}

                <div className="space-y-2">
                  {selectedCourier?.metadata?.breakdown?.extra && selectedCourier.metadata.breakdown.extra !== '₹0.00' && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Additional Slabs</span>
                      <span className="font-semibold text-xs text-blue-600">
                        {selectedCourier.metadata.breakdown.extra}
                      </span>
                    </div>
                  )}
                  <div className="border-t pt-2 flex justify-between">
                    <span className="font-semibold">Total Amount</span>
                    <span className="font-bold text-lg text-blue-600">
                      ₹{effectiveOrderTotal.toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Delhivery International — Shipment Category Picker */}
            {selectedCourier?.provider === 'delhivery_international' && (() => {
              const d = delhiveryIntlDetails;
              const update = (patch) => setDelhiveryIntlDetails(prev => ({ ...prev, ...patch }));
              // DOCUMENT service → only Non Commercial + Document
              if (selectedCourier?._courierId === 'EXPORTS_DOCUMENT' && d.shipmentCategory !== 'non_commercial') {
                update({ shipmentCategory: 'non_commercial', shipmentSubType: 'document' });
              }
              const isNonCommercial = d.shipmentCategory === 'non_commercial';
              const isCommercial = d.shipmentCategory === 'commercial';
              const sub = d.shipmentSubType;
              const needsBasicInvoice = sub === 'sample' || sub === 'gift';
              const needsFullCommercial = sub === 'cargo' || sub === 'csb5';
              const needsIGST = needsBasicInvoice || needsFullCommercial;
              const incoterms = needsFullCommercial
                ? ['EXW','FOB','FCA','CPT','CIF','DAT','DAP','DDP']
                : ['FOB'];
              const incotermLabels = {
                EXW:'Ex Works',FOB:'Free on Board',FCA:'Free Carrier',CPT:'Carriage Paid To',
                CIF:'Carriage, Insurance and Freight',DAT:'Delivered at Terminal',
                DAP:'Delivered at Place',DDP:'Delivered Duty Paid'
              };

              return (
              <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-3">
                <p className="text-xs font-medium text-gray-700">Choose Type of Shipment</p>

                {/* Category */}
                <div className="flex gap-4">
                  <label className="flex items-center gap-1.5 cursor-pointer">
                    <input type="radio" name="delhiveryCategory" className="mt-0.5"
                      checked={isNonCommercial}
                      onChange={() => update({ shipmentCategory: 'non_commercial', shipmentSubType: null })} />
                    <span className="text-sm font-medium">Non Commercial</span>
                  </label>
                  {selectedCourier?._courierId !== 'EXPORTS_DOCUMENT' && (
                  <label className="flex items-center gap-1.5 cursor-pointer">
                    <input type="radio" name="delhiveryCategory" className="mt-0.5"
                      checked={isCommercial}
                      onChange={() => update({ shipmentCategory: 'commercial', shipmentSubType: null })} />
                    <span className="text-sm font-medium">Commercial</span>
                  </label>
                  )}
                </div>

                {/* Sub-type */}
                {d.shipmentCategory && (
                  <div className="ml-4 space-y-1 border-l-2 border-gray-200 pl-3">
                    {(isNonCommercial ? [
                      { val:'document', label:'Document' },
                      { val:'sample', label:'Sample' },
                      { val:'gift', label:'Gift' },
                    ] : [
                      { val:'cargo', label:'Cargo' },
                      { val:'csb5', label:'Non Document / CSB5' },
                    ]).filter(s => {
                      const svc = selectedCourier?._courierId;
                      if (svc === 'EXPORTS_DOCUMENT') return s.val === 'document';
                      if (svc === 'EXPORTS_DLV_SAVER') return s.val === 'sample' || s.val === 'gift' || s.val === 'csb5';
                      if (svc === 'EXPORTS_EXPRESS') return s.val !== 'document';
                      return true; // DEFERRED or no selection = all options
                    }).map(s => (
                      <label key={s.val} className="flex items-center gap-1.5 cursor-pointer">
                        <input type="radio" name="delhiverySubType" className="mt-0.5"
                          checked={sub === s.val}
                          onChange={() => update({ shipmentSubType: s.val })} />
                        <span className="text-sm">{s.label}</span>
                      </label>
                    ))}
                  </div>
                )}

                {/* Additional Fields */}
                {sub && sub !== 'document' && (
                  <div className="space-y-3 border-t border-gray-100 pt-3">

                    {/* INCO Terms */}
                    <div className="space-y-1">
                      <label className="text-xs font-medium text-gray-700">International Commercial (INCO) Terms</label>
                      <select value={d.incoterm} onChange={e => update({ incoterm: e.target.value })}
                        className="w-full border border-gray-300 rounded-md p-2 text-sm">
                        {incoterms.map(t => <option key={t} value={t}>{t} — {incotermLabels[t]}</option>)}
                      </select>
                    </div>

                    {/* Invoice Number */}
                    <div className="space-y-1">
                      <label className="text-xs font-medium text-gray-700">Invoice Number</label>
                      <input type="text" value={d.invoiceNumber} onChange={e => update({ invoiceNumber: e.target.value })}
                        placeholder="Enter invoice number" className="w-full border border-gray-300 rounded-md p-2 text-sm" />
                    </div>

                    {/* Invoice Date */}
                    <div className="space-y-1">
                      <label className="text-xs font-medium text-gray-700">Invoice Date</label>
                      <input type="date" value={d.invoiceDate} onChange={e => update({ invoiceDate: e.target.value })}
                        className="w-full border border-gray-300 rounded-md p-2 text-sm" />
                    </div>

                    {/* CIF extras */}
                    {needsFullCommercial && d.incoterm === 'CIF' && (
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1">
                          <label className="text-xs font-medium text-gray-700">Insurance Value</label>
                          <input type="number" value={d.insuranceValue} onChange={e => update({ insuranceValue: e.target.value })}
                            placeholder="0" className="w-full border border-gray-300 rounded-md p-2 text-sm" />
                        </div>
                        <div className="space-y-1">
                          <label className="text-xs font-medium text-gray-700">Freight Value</label>
                          <input type="number" value={d.freightValue} onChange={e => update({ freightValue: e.target.value })}
                            placeholder="0" className="w-full border border-gray-300 rounded-md p-2 text-sm" />
                        </div>
                      </div>
                    )}

                    {/* IGST Payment Status */}
                    {needsIGST && (
                      <div className="space-y-1">
                        <label className="text-xs font-medium text-gray-700">IGST Payment Status</label>
                        <select value={d.igstPaymentStatus} onChange={e => update({ igstPaymentStatus: e.target.value })}
                          className="w-full border border-gray-300 rounded-md p-2 text-sm">
                          <option value="">Select</option>
                          <option value="Paid">Paid</option>
                          <option value="LUT">LUT</option>
                          <option value="Bond">Bond</option>
                        </select>
                        {(d.igstPaymentStatus === 'LUT' || d.igstPaymentStatus === 'Bond') && (
                          <input type="text" value={d.lutBondRef} onChange={e => update({ lutBondRef: e.target.value })}
                            placeholder={`${d.igstPaymentStatus} Reference Number`}
                            className="w-full border border-gray-300 rounded-md p-2 text-sm mt-1" />
                        )}
                      </div>
                    )}

                    {/* E-commerce checkbox (commercial only) */}
                    {needsFullCommercial && (
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input type="checkbox" checked={d.ecommerceExport}
                          onChange={e => update({ ecommerceExport: e.target.checked })} />
                        <span className="text-xs text-gray-700">Export using e-commerce</span>
                      </label>
                    )}

                    {/* IOR (commercial only) */}
                    {needsFullCommercial && (
                      <div className="space-y-2">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input type="checkbox" checked={d.hasIOR}
                            onChange={e => update({ hasIOR: e.target.checked })} />
                          <span className="text-xs font-medium text-gray-700">Have Importer On Record Details</span>
                        </label>
                        {d.hasIOR && (
                          <div className="ml-4 space-y-2 border-l-2 border-gray-200 pl-3">
                            <input type="text" value={d.iorName} onChange={e => update({ iorName: e.target.value })}
                              placeholder="Name" className="w-full border border-gray-300 rounded-md p-2 text-sm" />
                            <input type="text" value={d.iorPhone} onChange={e => update({ iorPhone: e.target.value })}
                              placeholder="Phone Number" className="w-full border border-gray-300 rounded-md p-2 text-sm" />
                            <input type="text" value={d.iorAddress} onChange={e => update({ iorAddress: e.target.value })}
                              placeholder="Shipping Address" className="w-full border border-gray-300 rounded-md p-2 text-sm" />
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Clear */}
                <button type="button" onClick={() => {
                  setDelhiveryIntlDetails({
                    shipmentCategory: null, shipmentSubType: null,
                    incoterm: 'FOB', invoiceNumber: '', invoiceDate: new Date().toISOString().split('T')[0],
                    igstPaymentStatus: '', lutBondRef: '', insuranceValue: '', freightValue: '',
                    ecommerceExport: false, hasIOR: false, iorName: '', iorPhone: '', iorAddress: '',
                  });
                  setSelectedCourierId(null);
                }} className="text-xs text-gray-500 hover:text-gray-700 underline">
                  Clear selection
                </button>
              </div>
            )})()}

            {calculatingRate && (
              <div className="bg-white border rounded-lg p-4 text-center">
                <Loader2 className="w-6 h-6 animate-spin text-blue-600 mx-auto" />
                <p className="text-sm text-gray-600 mt-2">Calculating rate...</p>
              </div>
            )}

            {/* Wallet Balance Info */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm font-medium text-gray-700">Wallet Balance</span>
                <span className="font-bold text-lg text-blue-600">₹{balance?.toFixed(2) || '0.00'}</span>
              </div>
              {rate && balance < effectiveOrderTotal && (
                <div className="flex items-center gap-2 text-xs text-red-600 mt-2">
                  <AlertCircle size={14} />
                  <span>Insufficient balance. Please recharge.</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Sticky Footer */}
        <div className="fixed bottom-0 left-0 right-0 bg-transparent p-4 flex justify-end gap-4 z-40">
          {bankHint && (
            <div className="flex items-center gap-2 text-xs text-red-600 mr-auto self-center">
              <AlertCircle size={14} />
              <span>{bankHint}</span>
            </div>
          )}
          <button
            onClick={() => navigate(-1)}
            className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 font-semibold hover:bg-gray-50 transition"
          >
            Cancel
          </button>
          <button
            onClick={handleCreateOrder}
            disabled={
              loading ||
              !rate ||
              calculatingRate ||
              (!rate?.courierOptions?.length || !selectedCourierId)
            }
            className="px-6 py-2 bg-blue-600 text-white rounded-lg font-semibold shadow-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition flex items-center gap-2"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Creating Order...
              </>
            ) : (
              <>
                <Check size={20} />
                Create Order
              </>
            )}
          </button>
        </div>

        {/* KYC Documents Required Modal (PAN / Aadhaar / IEC gate) */}
        {kycModal.open && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg p-6 max-w-md w-full">
              <div className="text-center">
                <AlertCircle className="w-16 h-16 text-orange-500 mx-auto mb-4" />
                <h2 className="text-2xl font-bold text-gray-900 mb-2">KYC Documents Required</h2>
                <p className="text-gray-600 text-base mb-4">
                  {kycModal.message || `You have not uploaded ${kycModal.missing.join(', ')}. Please upload ${kycModal.missing.join(', ')} to place this order.`}
                </p>
                {kycModal.missing.length > 0 && (
                  <div className="flex flex-wrap justify-center gap-2 mb-6">
                    {kycModal.missing.map(doc => (
                      <span key={doc} className="px-3 py-1.5 bg-red-50 border border-red-200 text-red-700 rounded-full text-sm font-semibold">
                        {doc}
                      </span>
                    ))}
                  </div>
                )}
                <div className="flex flex-col gap-2">
                  <button
                    onClick={() => { setKycModal({ open: false, missing: [], message: '' }); navigate('/settings?tab=documents'); }}
                    className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg hover:bg-blue-700 transition-colors font-semibold"
                  >
                    Upload Documents
                  </button>
                  <button
                    onClick={() => setKycModal({ open: false, missing: [], message: '' })}
                    className="w-full bg-gray-100 text-gray-700 py-3 px-4 rounded-lg hover:bg-gray-200 transition-colors font-semibold"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
        {/* Bank Account Modal (Delhivery International booking gate) */}
        <BankAccountModal
          isOpen={bankModalOpen}
          onClose={() => setBankModalOpen(false)}
          existingAccount={userBankAccount && userBankAccount.accountHolderName ? userBankAccount : null}
          onSaved={(acct) => {
            setUserBankAccount(acct);
            setBankHint('');
            setBankModalOpen(false);
            handleCreateOrder(); // auto-continue the booking after bank details are saved
          }}
        />

        {/* Recharge Modal */}
        <RechargeModal
          isOpen={showRechargeModal}
          onClose={() => setShowRechargeModal(false)}
          onSuccess={() => {
            fetchBalance();
            setShowRechargeModal(false);
          }}
        />

        {/* Price Breakdown Modal */}
        <PriceBreakdownModal
          isOpen={!!breakdownCourier}
          onClose={() => setBreakdownCourier(null)}
          courier={breakdownCourier}
        />
      </div>
    </KYCGuard>
  );
};

export default CreateOrder;
